/**
 * ConfirmModal — an accessible, animated confirmation dialog.
 *
 * Rendered through a portal so it always escapes overflow/stacking contexts.
 * Closes on backdrop click and Escape; the confirm action shows a loading
 * state while the (optionally async) handler runs.
 */
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'motion/react';
import { Icons } from '@/src/modules/components/Icons';
import { cn } from '@/src/infrastructure/lib/utils';

export function ConfirmModal({
  open,
  onClose,
  onConfirm,
  title,
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  tone = 'danger',
  /** When set, the user must type this exact string to enable the confirm button. */
  confirmPhrase,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void | Promise<void>;
  title: string;
  description?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: 'danger' | 'primary';
  confirmPhrase?: string;
}) {
  const [busy, setBusy] = useState(false);
  const [typed, setTyped] = useState('');

  useEffect(() => {
    if (!open) {
      setTyped('');
      setBusy(false);
      return;
    }
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && !busy && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, busy, onClose]);

  const phraseOk = !confirmPhrase || typed.trim() === confirmPhrase;

  async function handleConfirm() {
    if (!phraseOk) return;
    setBusy(true);
    try {
      await onConfirm();
      onClose();
    } finally {
      setBusy(false);
    }
  }

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <div
            className="absolute inset-0 bg-on-surface/40 backdrop-blur-sm"
            onClick={() => !busy && onClose()}
          />
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-label={title}
            initial={{ opacity: 0, scale: 0.94, y: 16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 8 }}
            transition={{ type: 'spring', stiffness: 320, damping: 28 }}
            className="relative w-full max-w-md rounded-[1.75rem] bg-surface-lowest p-6 shadow-2xl border border-outline-variant/30"
          >
            <div
              className={cn(
                'w-12 h-12 rounded-2xl flex items-center justify-center mb-4',
                tone === 'danger'
                  ? 'bg-status-failed/10 text-status-failed'
                  : 'bg-primary/10 text-primary',
              )}
            >
              <Icons.Warning size={22} />
            </div>
            <h2 className="text-lg font-bold text-on-surface">{title}</h2>
            {description && (
              <p className="text-sm text-on-surface-variant mt-1.5">{description}</p>
            )}

            {confirmPhrase && (
              <div className="mt-4">
                <label className="text-xs font-semibold text-on-surface-variant">
                  Type{' '}
                  <span className="font-bold text-on-surface">{confirmPhrase}</span> to
                  confirm
                </label>
                <input
                  autoFocus
                  value={typed}
                  onChange={(e: any) => setTyped(e.target.value)}
                  className="mt-1.5 w-full rounded-xl border border-outline-variant/50 bg-surface-low/60 px-3.5 py-2.5 text-sm outline-none focus:ring-2 focus:ring-status-failed/30 focus:border-status-failed"
                />
              </div>
            )}

            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={onClose}
                disabled={busy}
                className="px-4 py-2.5 rounded-xl text-sm font-semibold text-on-surface-variant hover:bg-surface-low transition-colors disabled:opacity-50"
              >
                {cancelLabel}
              </button>
              <button
                type="button"
                onClick={handleConfirm}
                disabled={busy || !phraseOk}
                className={cn(
                  'px-4 py-2.5 rounded-xl text-sm font-bold text-white transition-all active:scale-95 disabled:opacity-50 disabled:pointer-events-none inline-flex items-center gap-2',
                  tone === 'danger'
                    ? 'bg-status-failed hover:brightness-95'
                    : 'bg-primary hover:bg-primary-dark',
                )}
              >
                {busy && <Icons.Rocket size={15} className="animate-spin" />}
                {confirmLabel}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  );
}
